/* eslint-disable max-lines */
import { useState, useEffect, useRef, useCallback } from "react";
import { Modal, ModalHeader, ModalBody } from "@design-lab/shared/ui";
import {
  useCompositionForm,
  useCompositionsActions,
  useCurrentComposition
} from "@design-lab/entities/composition";
import { PRODUCT_LIST_PATH } from "@design-lab/shared/constants/constants";
import { useFetchPrintOnDemandProduct } from "@design-lab/entities/product";
import { useWarmChooseSizesPage } from "@design-lab/entities/artwork";
import { useTab } from "@design-lab/shared/ui/layouts/state";
import { notifyError } from "common/utils/app-monitoring/notify";

const WHOLESALE_LEARN_MORE_SELECTORS = ".js-wholesale-learn-more-pdp";
const boundWholesaleLearnMoreDocuments = new WeakSet<Document>();

type MessageEventData = MessageEvent<
  | {
      type: "designlabmodal:navigation:start" | "designlabmodal:navigation:end";
    }
  | {
      type: "designlabmodal:productadded";
      payload: {
        catalogId: string;
        colorName: string;
        compositionId: string;
        replace: boolean;
      };
    }
  | {
      type: "designlabmodal:productselected";
      payload: {
        catalogId: string;
      };
    }
  | {
      type: "designlabmodal:compositionselected";
      payload: {
        catalogId: string;
        compositionId: string;
      };
    }
>;

type ProductBrowserProps = {
  url?: string;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
};

export function ProductBrowser(props: ProductBrowserProps) {
  const { url = PRODUCT_LIST_PATH, isOpen, onClose, title = "Add a Product" } = props;
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { setTab } = useTab();
  const { currentComposition } = useCurrentComposition();
  const { form } = useCompositionForm();
  const { addComposition, removeComposition } = useCompositionsActions();
  const fetchProduct = useFetchPrintOnDemandProduct();
  const warmChooseSizesPage = useWarmChooseSizesPage();

  const openResellerPricingModal = useCallback(() => {
    const $modal = document.querySelector<HTMLDialogElement>(".js-reseller-pricing-modal");
    if (!$modal) return;

    $modal.showModal();
    document.documentElement.classList.add("locked-all");
  }, []);

  const bindWholesaleLearnMoreInsideIframe = useCallback(() => {
    const $iframe = iframeRef.current;
    if (!$iframe) return;

    let iframeDocument: Document | null = null;
    try {
      iframeDocument = $iframe.contentDocument;
    } catch {
      iframeDocument = null;
    }

    if (!iframeDocument) return;
    if (boundWholesaleLearnMoreDocuments.has(iframeDocument)) return;
    boundWholesaleLearnMoreDocuments.add(iframeDocument);

    iframeDocument.addEventListener(
      "click",
      e => {
        const target = e.target as Element | null;
        const $trigger = target?.closest?.(WHOLESALE_LEARN_MORE_SELECTORS);
        if (!$trigger) return;

        e.preventDefault();
        e.stopPropagation();
        openResellerPricingModal();
      },
      true
    );
  }, [openResellerPricingModal]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    bindWholesaleLearnMoreInsideIframe();
  };

  useEffect(() => {
    // Listen for messages from the iframe
    const handleMessage = (event: MessageEventData) => {
      if (event.origin !== window.location.origin) return;
      if (!isOpen) return;

      if (event.data.type === "designlabmodal:navigation:start") {
        setIsLoading(true);
      } else if (event.data.type === "designlabmodal:navigation:end") {
        setIsLoading(false);
      } else if (event.data.type === "designlabmodal:productadded") {
        const { replace, ...payload } = event.data.payload;
        if (replace) {
          const currentCompositionId = currentComposition?.id;
          addComposition(payload);

          // Only run the cross-product transfer + removal when actually swapping
          // to a different composition. Replacing a composition with itself would
          // otherwise schedule a removal of the just-selected composition.
          if (currentCompositionId && currentCompositionId !== payload.compositionId) {
            const currentCompositions = form.getValues("addedCompositions");
            const currentCompositionVariants =
              currentCompositions?.find(c => c.id === currentCompositionId)?.variants ?? {};
            const selectedVariants = Object.keys(currentCompositionVariants);
            const selectedSizes =
              currentComposition?.variants?.filter(v => selectedVariants.includes(v.id)) ?? [];

            fetchProduct(payload.catalogId)
              .then(product => {
                // Resolve the index against the live form values rather than a
                // value captured before the await — the modal closes synchronously,
                // so the user may have mutated `addedCompositions` while the fetch
                // was in flight, which would make a pre-captured index stale.
                const newCompositionIndex =
                  form
                    .getValues("addedCompositions")
                    ?.findIndex(c => c.id === payload.compositionId) ?? -1;

                if (newCompositionIndex >= 0) {
                  const productCompositionVariants = product?.compositions?.find(
                    c => c.id === payload.compositionId
                  )?.variants;
                  const selectedSizesVariants = selectedSizes.map(v => v.size);
                  const variantsToUpdateBySize = productCompositionVariants
                    ?.filter(v => selectedSizesVariants.includes(v.size))
                    ?.reduce<Record<string, string>>((acc, v) => {
                      acc[v.size] = v.id;
                      return acc;
                    }, {});

                  selectedSizes.forEach(v => {
                    const quantity = currentCompositionVariants[v.id];
                    const variantId = variantsToUpdateBySize?.[v.size];

                    if (quantity && variantId) {
                      form.setValue(
                        `addedCompositions.${newCompositionIndex}.variants.${variantId}`,
                        quantity
                      );
                    }
                  });
                }
              })
              .catch(error => {
                // fetchProduct failed (network blip / 5xx / timeout). There is no
                // product data to transfer variant quantities from, so the transfer
                // is skipped — the old composition is still removed in `finally`, so
                // the form never ends up holding both compositions. Report the
                // failure so the silently-dropped variant transfer is observable.
                notifyError("ProductBrowser: fetchProduct failed during composition replace", {
                  params: {
                    catalog_id: payload.catalogId,
                    composition_id: payload.compositionId,
                    error: error instanceof Error ? error.message : String(error)
                  }
                });
              })
              .finally(() => {
                removeComposition(currentCompositionId);
              });
          }
        } else {
          setTab("size");
          addComposition(payload);
        }
        onClose();
      } else if (event.data.type === "designlabmodal:productselected") {
        const { catalogId } = event.data.payload;
        fetchProduct(catalogId)
          .then(product => {
            if (product?.id) {
              // Warm cache for inventory and price data using product id
              warmChooseSizesPage(product.id);
            }
          })
          .catch(error => {
            // Best-effort cache warm; a fetchProduct rejection (network / 5xx /
            // timeout) must not surface as an unhandled promise rejection.
            notifyError("ProductBrowser: fetchProduct failed during productselected", {
              params: {
                catalog_id: catalogId,
                error: error instanceof Error ? error.message : String(error)
              }
            });
          });
      } else if (event.data.type === "designlabmodal:compositionselected") {
        const { catalogId, compositionId } = event.data.payload;

        fetchProduct(catalogId)
          .then(product => {
            if (product?.id) {
              // Warm cache for inventory and price data using product id
              warmChooseSizesPage(product.id);
            }

            const composition = product?.compositions?.find(c => c.id === compositionId);

            if (!composition) return undefined;
            return fetch(
              composition.surfaces?.front?.picture || composition.surfaces?.frontLeftChest?.picture
            );
          })
          .catch(error => {
            // Best-effort cache warm + picture prefetch; a rejection from
            // fetchProduct or the picture fetch must not surface as an unhandled
            // promise rejection.
            notifyError("ProductBrowser: fetchProduct failed during compositionselected", {
              params: {
                catalog_id: catalogId,
                composition_id: compositionId,
                error: error instanceof Error ? error.message : String(error)
              }
            });
          });
      }
    };

    window.addEventListener("message", handleMessage);

    // Clean up the event listener
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    addComposition,
    currentComposition?.id,
    currentComposition?.variants,
    fetchProduct,
    form,
    isOpen,
    onClose,
    removeComposition,
    setTab,
    warmChooseSizesPage
  ]);

  // Reset loading state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="flex h-[96dvh] w-[92vw] flex-col"
      closeOnBackdropClick={false}
      closeOnEsc={false}
    >
      <ModalHeader onClose={onClose}>{title}</ModalHeader>
      <ModalBody className="relative flex-1 p-0">
        {isLoading && (
          <div className="bg-background absolute inset-0 z-10 flex items-center justify-center bg-opacity-80">
            <div className="flex flex-col items-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
              <p className="mt-2 text-gray-600">Loading</p>
            </div>
          </div>
        )}
        {isOpen && (
          <iframe
            ref={iframeRef}
            src={url}
            className="size-full border-0"
            title="Product Catalog"
            onLoad={handleIframeLoad}
          />
        )}
      </ModalBody>
    </Modal>
  );
}
